import { BehaviorSubject, combineLatest, filter, map, Observable, of, switchMap } from 'rxjs';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';

import { BrandsEnum } from '@seb/web-design-system/core';

import { BrandsCorner, WC_CONFIG_PROVIDER } from '../models';
import { CustomBrandService } from './custom-brand.service';
import { BaseStoreService } from './base-store.service';
import { getBrandConfig } from '../web-common.config';
import { HobMapper } from '../mappers/hob.mapper';

const brandColorsName: { primary: string[], error: string[], success: string[], themes: string[] } = {
  primary: ['brand', 'light', 'base', 'hover', 'active', 'dark'],
  error: ['base', 'hover', 'active'],
  success: ['base', 'hover', 'active'],
  themes: [
    'very-light',
    'light',
    'regular',
    'colored',
    'promo-1',
    'promo-2',
    'stars',
    'extra',
    'dark'
  ]
} as const;

const themeVarsName: string[] = [
  '--brand--logo-color-background-brand',
  '--brand--logo-color-background-light',
  '--brand--logo-color-background-dark',
  '--brand--link-color',
  '--background-dark',
  '--brand--border-radius',
  '--brand--icon-border-radius',
  '--brand--button-color',
  '--brand--button-secondary-color',
  '--brand--button-secondary-border',
  '--brand--button-uppercase',
  '--brand--border-radius-global',
  '--brand--border-radius-global-large',
  '--brand--border-radius-tag',
  '--brand--border-radius-form',
  '--brand--active-bar', // *
  '--font-default',
  '--brand--font-family',
  '--color-tips',
  '--brand--border-menu', // *
  '--color--tips', // *
  '--color-skeeper-background', // *
  '--color-skeeper-rating', // *
  '--color-push-card-banner-dark',
  '--color-push-card-banner-button-dark',
  '--color-push-card-banner-button-text-dark',
  '--color-highlight-menu', // *
  '--color-highlight-menu-dark', // *
  '--color-price-discounted'
] as const;

interface HobStateInterface {
  activeBrands: BrandsEnum[];
  isRecipesEnabled: boolean;
  liveBrand?: BrandsEnum;
  siteBrand?: BrandsEnum;
  isHob: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class HobStateService {
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly wcConfig = inject(WC_CONFIG_PROVIDER);
  private readonly brandService = inject(CustomBrandService);
  private readonly baseStoreService = inject(BaseStoreService);
  private readonly hobMapper = inject(HobMapper);

  private readonly hobStateSubject = new BehaviorSubject<HobStateInterface>({
    activeBrands: undefined,
    isRecipesEnabled: true,
    liveBrand: undefined,
    siteBrand: undefined,
    isHob: false
  });

  constructor() {
    this.brandService.getFullBrands().pipe(
      filter((brands) => !!brands),
      switchMap((brands) => combineLatest([
        of(brands), this.brandService.getActive()
      ]))
    ).subscribe(([brands, brand]) => {
      this.setHobState(brands, brand as BrandsEnum);
    });
  }

  get hobState(): HobStateInterface {
    return this.hobStateSubject.value;
  }

  get hobState$(): Observable<HobStateInterface> {
    return this.hobStateSubject.asObservable();
  }

  get isHob$(): Observable<boolean> {
    return this.hobState$.pipe(map((state) => state.isHob));
  }

  get isRecipeEnabled$(): Observable<boolean> {
    return this.hobState$.pipe(map((state) => state.isRecipesEnabled));
  }

  getActiveBrands(): BrandsEnum[] | undefined {
    const { isHob, activeBrands } = this.hobStateSubject.value;
    return isHob ? activeBrands : undefined;
  }

  /**
   * Initialize or update the HOB state
   * @param brands The allowed brands list
   * @param brand The current Active brand
   */
  private setHobState(brands: BrandsCorner[] = undefined, brand?: BrandsEnum): void {
    const newState = { ...this.hobState };
    let changed = false;

    if (newState.activeBrands === undefined) {
      newState.activeBrands = brands?.map(({ id }) => this.hobMapper.toBrandEnum(id));

      if (newState.activeBrands?.length) {
        newState.isRecipesEnabled = newState.activeBrands.some((b) => getBrandConfig(b)?.isRecipesEnabled) ?? false;
        newState.isHob = true;
      } else {
        newState.isRecipesEnabled = this.wcConfig?.isRecipesEnabled ?? true;
        newState.isHob = false;
      }

      newState.siteBrand = this.baseStoreService.baseStore.brandSite as BrandsEnum;
      changed = true;
    }

    if (newState.isHob && (brand !== newState.liveBrand)) {
      newState.liveBrand = brand?.toUpperCase() as BrandsEnum;
      this.setHobTheme();
      changed = true;
    }

    if (changed) {
      this.hobStateSubject.next(newState);
    }
  }

  /**
   * Change the main document styles according to the given brand name
   * @param brand The new brand
   */
  private setHobTheme(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const computedStyle = getComputedStyle(this.document.documentElement);
    const brandName = '-' + BrandsEnum.hob.toLowerCase();
    const cssProperties: Record<string, string> = {};
    const element = this.document.body;

    Object.entries(brandColorsName).forEach(([key, values]) => {
      values.forEach((colorName) => {
        cssProperties[`--color-${key}-${colorName}`] = computedStyle.getPropertyValue(`--color-${key}-${colorName}${brandName}`);
        cssProperties[`--color-${key}-${colorName}-h`] = computedStyle.getPropertyValue(`--color-${key}-${colorName}${brandName}-h`);
        cssProperties[`--color-${key}-${colorName}-s`] = computedStyle.getPropertyValue(`--color-${key}-${colorName}${brandName}-s`);
        cssProperties[`--color-${key}-${colorName}-l`] = computedStyle.getPropertyValue(`--color-${key}-${colorName}${brandName}-l`);
      });
    });

    themeVarsName.forEach((cssVar) => {
      const cssVarValue = computedStyle.getPropertyValue(`${cssVar}${brandName}`);
      cssProperties[cssVar] = cssVarValue;
    });

    Object.entries(cssProperties).forEach(([property, value]) => {
      element.style.setProperty(property, value);
    });
  }
}
